import React, { useState, useEffect } from "react";
import { Award, X, Loader2 } from "lucide-react";
import axios from "axios";
import { logger } from "../utils/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IncentivesModal = ({ isOpen, onClose, auth }) => {
  const [incentives, setIncentives] = useState(null);
  const [loading, setLoading] = useState(true);

  const authAxios = axios.create({
    withCredentials: true
  });

  useEffect(() => {
    if (isOpen) {
      fetchIncentives();
    }
  }, [isOpen]);

  const fetchIncentives = async () => {
    setLoading(true);
    try {
      const response = await authAxios.get(`${API_URL}/api/admin/incentives`);
      setIncentives(response.data.executives);
    } catch (err) {
      logger.error("Failed to fetch incentives");
    } finally {
      setLoading(false);
    }
  };

  const handlePayIncentive = async (bookingId) => {
    try {
      await authAxios.put(`${API_URL}/api/admin/bookings/${bookingId}/pay-incentive`);
      fetchIncentives();
    } catch (err) {
      logger.error("Failed to pay incentive");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-xl p-6 w-full max-w-4xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-[#1E1B4B] font-['Outfit'] flex items-center gap-2">
            <Award size={24} className="text-[#E83E8C]" /> Sales Incentives
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={20} className="text-[#475569]" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-[#E83E8C]" />
          </div>
        ) : !incentives || Object.keys(incentives).length === 0 ? (
          <p className="text-center text-[#475569] py-10">No completed bookings with sales executives yet</p>
        ) : (
          <div className="space-y-6">
            {Object.entries(incentives).map(([execName, data]) => (
              <div key={execName} className="border rounded-2xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {execName.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1E1B4B]">{execName}</h3>
                      <p className="text-sm text-[#475569]">{data.total_bookings} completed bookings</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-[#475569]">Total Incentive</p>
                    <p className="text-2xl font-bold text-[#1E1B4B]">₹{data.total_incentive}</p>
                    <div className="flex gap-4 mt-1 text-sm">
                      <span className="text-green-600">Paid: ₹{data.paid_incentive}</span>
                      <span className="text-orange-600">Pending: ₹{data.pending_incentive}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {data.bookings.map((booking) => (
                    <div key={booking.id} className="flex items-center justify-between bg-[#FFFAF5] rounded-xl p-3">
                      <div>
                        <p className="font-medium text-[#1E1B4B]">{booking.customer_name || "Customer"}</p>
                        <p className="text-sm text-[#475569]">{booking.date} • {booking.package}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-[#1E1B4B]">₹{booking.incentive_amount}</span>
                        {booking.incentive_paid ? (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">Paid</span>
                        ) : (
                          <button onClick={() => handlePayIncentive(booking.id)}
                            className="px-3 py-1 bg-[#E83E8C] text-white rounded-full text-sm font-medium hover:opacity-90">Mark Paid</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default IncentivesModal;
